import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, Moon, Sun, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { NotificationCenter } from '@/components/NotificationCenter'
import { GlobalSearch } from '@/components/GlobalSearch'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { APP_NAME } from '@/lib/constants'

interface HeaderProps {
  onMenuClick: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { effectiveTheme, setTheme } = useTheme()

  const toggleTheme = () => {
    setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        {/* Left: Menu button + Logo */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onMenuClick} className="lg:hidden">
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center text-white font-bold">
              RC
            </div>
            <span className="hidden font-semibold sm:inline-block">{APP_NAME}</span>
          </Link>
        </div>

        {/* Center: Global Search (desktop) */}
        <div className="hidden md:flex flex-1 max-w-lg mx-8">
          <GlobalSearch className="w-full" />
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="minimal" />

          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {effectiveTheme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          <NotificationCenter />

          <div className="relative group">
            <Button variant="ghost" size="icon" className="rounded-full">
              <User className="h-5 w-5" />
            </Button>

            {/* Dropdown menu */}
            <div className="absolute right-0 mt-2 w-48 rounded-md border bg-popover p-1 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <div className="px-3 py-2 border-b">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <Link
                to="/profile"
                className="block px-3 py-2 text-sm hover:bg-accent rounded-sm"
              >
                {t('nav.profile')}
              </Link>
              <Link
                to="/settings"
                className="block px-3 py-2 text-sm hover:bg-accent rounded-sm"
              >
                {t('nav.settings')}
              </Link>
              <button
                onClick={logout}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm text-destructive"
              >
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
